import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "../lib/api";
import { Modal, Field, Badge, Spinner, Confirm } from "../components/ui";
import { COULEUR_BADGE } from "../components/RefSelect";

const ORDRE_LISTES = ["pays", "marche_vertical", "source_lead", "statut_lead", "motif_perte"];

const AIDE: Record<string, string> = {
  pays: "Utilisé sur les fiches organisation, contact et lead. Chaque pays appartient à un groupe (Afrique francophone / Reste du monde).",
  marche_vertical: "Proposé sur les organisations de type client et revendeur.",
  source_lead: "Source d'un lead. « Autre » est une valeur système : elle impose une précision libre et ne peut être ni désactivée ni supprimée.",
  statut_lead: "Le libellé, l'ordre et la couleur sont modifiables ; le code technique porte la logique métier (« Dans le pipe » est assigné automatiquement à la conversion d'un lead en devis) et reste fixe.",
  motif_perte: "Motif obligatoire pour passer un devis en Perdu. « Autre » est une valeur système qui impose un commentaire.",
};

export function ListesValeurs() {
  const [data, setData] = useState<any>(null);
  const [liste, setListe] = useState("pays");
  const [usages, setUsages] = useState<Record<string, number>>({});
  const [q, setQ] = useState("");
  const [form, setForm] = useState<any>({ libelle: "", groupe: "Afrique francophone", couleur: "neutre" });
  const [edit, setEdit] = useState<any>(null);
  const [del, setDel] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    Promise.all([api.get("/referentiels/listes"), api.get(`/referentiels/${liste}/usages`)]).then(([r, u]) => {
      setData(r.data);
      setUsages(u.data);
    });
  useEffect(() => {
    setErr(null);
    setInfo(null);
    setQ("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liste]);

  const valeurs: any[] = useMemo(() => data?.listes?.[liste] || [], [data, liste]);
  const visibles = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? valeurs.filter((v) => v.libelle.toLowerCase().includes(n) || (v.code || "").includes(n)) : valeurs;
  }, [valeurs, q]);

  if (!data) return <Spinner />;

  const proteges: string[][] = data.proteges || [];
  const estProtege = (v: any) => proteges.some(([l, c]) => l === liste && c === v.code);
  const couleurs: string[] = data.couleurs || [];
  const groupesPays: string[] = data.groupes_pays || [];

  const run = async (fn: () => Promise<any>, message?: (r: any) => string) => {
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      const r = await fn();
      if (message) setInfo(message(r?.data));
      await load();
      return true;
    } catch (e) {
      setErr(apiError(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const ajouter = async () => {
    const body: any = { libelle: form.libelle.trim() };
    if (liste === "pays") body.groupe = form.groupe;
    if (liste === "statut_lead") body.couleur = form.couleur;
    if (await run(() => api.post(`/referentiels/${liste}`, body), (r) => `« ${r.libelle} » ajoutée (code technique ${r.code}).`)) setForm({ ...form, libelle: "" });
  };

  const renommer = async () => {
    const body: any = { libelle: edit.libelle.trim() };
    if (liste === "pays") body.groupe = edit.groupe;
    if (liste === "statut_lead") body.couleur = edit.couleur;
    if (await run(() => api.patch(`/referentiels/${liste}/${edit.id}`, body), (r) => `« ${r.libelle} » enregistrée — ${r.fiches_mises_a_jour} fiche(s) mise(s) à jour.`)) setEdit(null);
  };

  const basculer = (v: any) =>
    run(() => api.patch(`/referentiels/${liste}/${v.id}`, { actif: !v.actif }), (r) => (r.actif ? `« ${r.libelle} » réactivée.` : `« ${r.libelle} » désactivée : plus proposée à la saisie, conservée sur les ${usages[r.libelle] || 0} fiche(s) qui la portent.`));

  const supprimer = async () => {
    const v = del;
    setDel(null);
    await run(() => api.delete(`/referentiels/${liste}/${v.id}`), () => `« ${v.libelle} » supprimée.`);
  };

  const deplacer = (idx: number, sens: -1 | 1) => {
    const ids = valeurs.map((v) => v.id);
    const j = idx + sens;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    run(() => api.put(`/referentiels/${liste}/ordre`, { ids }));
  };

  return (
    <div data-testid="listes-valeurs" className="space-y-4">
      <div className="flex flex-wrap gap-1 rounded-xl border border-line bg-white p-1">
        {ORDRE_LISTES.map((k) => (
          <button
            key={k}
            data-testid={`liste-tab-${k}`}
            onClick={() => setListe(k)}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${liste === k ? "bg-green text-white" : "text-muted hover:bg-soft"}`}
          >
            {data.libelles?.[k] || k}
            <span className="ml-1.5 text-xs opacity-70">({(data.listes?.[k] || []).filter((v: any) => v.actif).length})</span>
          </button>
        ))}
      </div>

      <div className="nx-card p-4">
        <h2 className="text-sm font-bold text-green">{data.libelles?.[liste]}</h2>
        <p className="mb-3 mt-1 text-xs text-muted">{AIDE[liste]} Une valeur utilisée par des enregistrements ne peut pas être supprimée : désactivez-la, elle reste affichée sur les fiches qui la portent.</p>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Nouvelle valeur">
            <input data-testid="liste-ajout-input" className="nx-input w-64" value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} onKeyDown={(e) => e.key === "Enter" && form.libelle.trim() && ajouter()} />
          </Field>
          {liste === "pays" && (
            <Field label="Groupe">
              <select data-testid="liste-ajout-groupe" className="nx-input" value={form.groupe} onChange={(e) => setForm({ ...form, groupe: e.target.value })}>
                {groupesPays.map((g) => <option key={g}>{g}</option>)}
              </select>
            </Field>
          )}
          {liste === "statut_lead" && (
            <Field label="Couleur du badge">
              <select data-testid="liste-ajout-couleur" className="nx-input" value={form.couleur} onChange={(e) => setForm({ ...form, couleur: e.target.value })}>
                {couleurs.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
          )}
          <button data-testid="liste-ajout-btn" className="nx-btn-primary" disabled={busy || !form.libelle.trim()} onClick={ajouter}>Ajouter</button>
          <input data-testid="liste-recherche-input" className="nx-input ml-auto w-56" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {err && <p data-testid="liste-error" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-danger">{err}</p>}
        {info && <p data-testid="liste-info" className="mt-3 rounded-lg bg-lime-soft px-3 py-2 text-sm text-green">{info}</p>}
      </div>

      <div className="nx-card overflow-x-auto">
        <table className="nx-table">
          <thead>
            <tr>
              <th className="w-24">Ordre</th>
              <th>Libellé</th>
              <th>Code technique</th>
              {liste === "pays" && <th>Groupe</th>}
              {liste === "statut_lead" && <th>Badge</th>}
              <th className="text-right">Fiches</th>
              <th>État</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((v) => {
              const idx = valeurs.indexOf(v);
              const n = usages[v.libelle] || 0;
              const protege = estProtege(v);
              return (
                <tr key={v.id} data-testid={`liste-row-${v.code}`} className={v.actif ? "" : "opacity-60"}>
                  <td>
                    <div className="flex items-center gap-1">
                      <button data-testid={`liste-up-${v.code}`} className="nx-btn-ghost !px-1.5 !py-0.5 text-xs" disabled={busy || idx === 0 || !!q} title="Monter" onClick={() => deplacer(idx, -1)}>↑</button>
                      <button data-testid={`liste-down-${v.code}`} className="nx-btn-ghost !px-1.5 !py-0.5 text-xs" disabled={busy || idx === valeurs.length - 1 || !!q} title="Descendre" onClick={() => deplacer(idx, 1)}>↓</button>
                      <span className="ml-1 text-xs text-muted">{idx + 1}</span>
                    </div>
                  </td>
                  <td className="font-semibold" data-testid={`liste-libelle-${v.code}`}>
                    {v.libelle}
                    {protege && <Badge className="ml-2 bg-green text-white">Système</Badge>}
                  </td>
                  <td><code className="rounded bg-soft px-1.5 py-0.5 text-xs text-muted">{v.code}</code></td>
                  {liste === "pays" && <td className="text-sm text-muted">{v.groupe}</td>}
                  {liste === "statut_lead" && <td><Badge className={COULEUR_BADGE[v.couleur || "neutre"]}>{v.libelle}</Badge></td>}
                  <td className="text-right" data-testid={`liste-usages-${v.code}`}>{n}</td>
                  <td>{v.actif ? <Badge className="bg-lime-soft text-green">Active</Badge> : <Badge className="bg-gray-100 text-gray-700">Désactivée</Badge>}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <button data-testid={`liste-edit-${v.code}`} className="nx-btn-ghost !px-2 !py-1 text-xs" disabled={busy} onClick={() => setEdit({ ...v })}>Renommer</button>
                      <button
                        data-testid={`liste-toggle-${v.code}`}
                        className="nx-btn-ghost !px-2 !py-1 text-xs"
                        disabled={busy || (protege && v.actif)}
                        title={protege ? "Valeur système : ne peut pas être désactivée" : undefined}
                        onClick={() => basculer(v)}
                      >
                        {v.actif ? "Désactiver" : "Réactiver"}
                      </button>
                      <button
                        data-testid={`liste-delete-${v.code}`}
                        className="nx-btn-danger !px-2 !py-1 text-xs"
                        disabled={busy || protege || n > 0}
                        title={protege ? "Valeur système : ne peut pas être supprimée" : n > 0 ? `Utilisée par ${n} enregistrement(s) : désactivez-la plutôt` : "Supprimer définitivement"}
                        onClick={() => setDel(v)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {visibles.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-sm text-muted">Aucune valeur</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={`Renommer « ${edit.libelle} »`} onClose={() => setEdit(null)} testId="liste-edit-modal">
          <div className="space-y-3">
            <Field label="Libellé">
              <input data-testid="liste-edit-input" className="nx-input" value={edit.libelle} onChange={(e) => setEdit({ ...edit, libelle: e.target.value })} />
            </Field>
            {liste === "pays" && (
              <Field label="Groupe">
                <select data-testid="liste-edit-groupe" className="nx-input" value={edit.groupe || ""} onChange={(e) => setEdit({ ...edit, groupe: e.target.value })}>
                  {groupesPays.map((g) => <option key={g}>{g}</option>)}
                </select>
              </Field>
            )}
            {liste === "statut_lead" && (
              <Field label="Couleur du badge">
                <select data-testid="liste-edit-couleur" className="nx-input" value={edit.couleur || "neutre"} onChange={(e) => setEdit({ ...edit, couleur: e.target.value })}>
                  {couleurs.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
            )}
            <p className="text-xs text-muted">
              Code technique <code className="rounded bg-soft px-1">{edit.code}</code> (fixe). Le nouveau libellé sera propagé aux {usages[edit.libelle] || 0} fiche(s) qui portent la valeur actuelle.
            </p>
          </div>
          {err && <p data-testid="liste-edit-error" className="mt-3 text-sm text-danger">{err}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button className="nx-btn-ghost" onClick={() => setEdit(null)}>Annuler</button>
            <button data-testid="liste-edit-save-btn" className="nx-btn-primary" disabled={busy || !edit.libelle.trim()} onClick={renommer}>Enregistrer</button>
          </div>
        </Modal>
      )}

      {del && <Confirm message={`Supprimer définitivement « ${del.libelle} » de la liste ${data.libelles?.[liste]} ? Aucune fiche ne porte cette valeur.`} onConfirm={supprimer} onCancel={() => setDel(null)} />}
    </div>
  );
}
